import React, { useState, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import Toast from '../Toast';
import { FeedbackToolWrapper } from '../Shared/FeedbackTool';
import ImprovedSecondarySideBar from '../ImprovedSecondarySideBar';
import MenuButton from '../MenuButton';
import { isTabletView } from '../ReactiveRender';
import CommentBankCard from './CommentBankCard';
import CommentBankDialog from '../Shared/Dialogs/commentBank';
import {
  PopUpCard,
  PopUpCardImg,
  PopUpCardText,
} from '../Shared/FeedbackTool/style';
import {
  PopupBackground,
  PopupContainer,
  PopupTitleContainer,
  PopupTitle,
  PopupTitleImg,
  PopupDialogContentBox,
  PopupDialogContentBoxLeft,
  PopupDialogContentBoxRight,
  CardContainer,
  Card,
  CardImgCont,
  CardImg,
  CardTitle,
  ImportFileLabel,
  ImportFile,
  CardImgDoc,
  BankCommentTitle,
  PreviewContainer,
  PreviewIconCont,
  PrevieImg,
  Previewpara,
  CreateButton,
  ButtonText,
  Commentsuggestion,
} from '../Shared/Dialogs/popupStyle';
import {
  importJsonFile,
  exportJsonFile,
} from '../../components2/markingCriteria';
import { isNullOrEmpty } from '../../utils/arrays';
import {
  createNewFeedbackBank,
  deleteSmartAnnotation,
  updateCommentBankPreferences,
} from '../../service';
import { buildClonePayload } from '../../utils/subjects';
import { useApiSubjects } from '../../hooks/useSubjects';
import {
  useCommentBanks,
  useCommentBanksWithPreferences,
} from '../state/hooks';
import Loader from '../Loader';
import PlusIcon from '../../static/img/Plus-blue.svg';
import DownloadIcon from '../../static/img/Download.svg';
import closecircle from '../../static/img/closecircle.svg';
import Doc from '../../static/img/doc.svg';
import PreviewIcon from '../../static/img/preview.svg';
import UploadIcon from '../../static/icons/share-download-icon.svg';

function CommentBanks() {
  const history = useHistory();
  const queryClient = useQueryClient();
  const [selectedCommentBanks, setSelectedCommentBanks] = useState(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [isShowSystemCommentBanksOnly, setIsShowSystemCommentBanksOnly] =
    useState(false);
  const [importedCommentBank, setImportedCommentBank] = useState({});
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedBank, setSelectedBank] = useState();
  const [commentBankPreviewDialog, setCommentBankPreviewDialog] =
    useState(false);
  const [previewCommentBank, setPreviewCommentBank] = useState(null);
  const selectedRef = useRef(null);
  const tabletView = isTabletView();

  const {
    data: commentBanksData,
    isLoadingdata: isLoadingCommentBanks,
    setData: setCommentBanks,
    resetData: resetCommentBanks,
  } = useCommentBanks();

  const {
    data: commentBanksWithPreferences,
    isLoadingdata: isLoadingCommentBanksWithPreferences,
    setData: setCommentBanksWithPreferences,
    resetData: resetCommentBanksWithPreferences,
  } = useCommentBanksWithPreferences();
  const { apiSubjects } = useApiSubjects();

  const hasNoData = !commentBanksData && !commentBanksWithPreferences;
  const originalCommentBanks = commentBanksData?._embedded?.commentbanks || [];
  const systemSmartAnnotations = originalCommentBanks.filter(
    (bank) => bank.isSystem
  );

  const hasActiveFilters = useMemo(() => {
    return isShowSystemCommentBanksOnly || showHidden;
  }, [isShowSystemCommentBanksOnly, showHidden]);

  const isDragDisabled =
    hasActiveFilters || isLoadingCommentBanksWithPreferences;

  const handleSelectCommentBank = (commentBankId) => {
    const newSelected = new Set(selectedCommentBanks);
    if (newSelected.has(commentBankId)) {
      newSelected.delete(commentBankId);
    } else {
      newSelected.add(commentBankId);
    }
    setSelectedCommentBanks(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedCommentBanks.size === (filteredItems?.length || 0)) {
      setSelectedCommentBanks(new Set());
    } else {
      const allIds = filteredItems?.map((cb) => cb.id) || [];
      setSelectedCommentBanks(new Set(allIds));
    }
  };

  const handleFilterSystemOnes = () => {
    setIsShowSystemCommentBanksOnly(!isShowSystemCommentBanksOnly);
  };

  const handleCommentBankImport = async (event) => {
    try {
      const importedJsonFile = await importJsonFile(event, ['cb']);
      setImportedCommentBank(importedJsonFile);
      setShowImportDialog(true);
    } catch (err) {
      console.error('Error importing comment bank:', err);
    }
  };

  const createFeedbackBank = (feedbackBank = undefined, onClose = null) => {
    const newBank = {
      title: 'Untitled feedback bank',
      smartComments: [
        {
          title: 'First feedback area',
          suggestions: ['First comment', 'Second comment'],
        },
        {
          title: 'Second feedback area',
          suggestions: ['First comment.', 'Second comment.'],
        },
      ],
    };

    createNewFeedbackBank(feedbackBank || newBank)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'commentBanks'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'commentBanks'],
        });
        resetCommentBanks();
        resetCommentBanksWithPreferences();
        toast(<Toast message={'New feedback bank created'} />);

        if (onClose) {
          onClose();
        }

        const selfLink = response?._links?.self?.href;
        if (selfLink) {
          const newCommentBankId = selfLink.split('/').pop();
          history.push(`/tools/commentbanks/${newCommentBankId}`);
        }
      })
      .catch((error) => {
        console.error('Error creating new feedback bank:', error);
      });
  };

  const createSystemFeedbackBank = (commentBank, onClose = null) => {
    if (!commentBank) {
      toast(<Toast message={'Please select a template'} />);
      return;
    }

    const { smartComments } = commentBank;
    const newObject = buildClonePayload(commentBank, apiSubjects, {
      smartComments,
    });

    createNewFeedbackBank(newObject)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'commentBanks'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'commentBanks'],
        });

        resetCommentBanks();
        resetCommentBanksWithPreferences();
        toast(<Toast message={'New feedback bank created'} />);

        if (onClose) {
          onClose();
        }

        const selfLink = response?._links?.self?.href;
        if (selfLink) {
          const newCommentBankId = selfLink.split('/').pop();
          history.push(`/tools/commentbanks/${newCommentBankId}`);
        }
      })
      .catch((error) => {
        console.error('Error creating new feedback bank:', error);
      });
  };

  const cloneCommentBank = (commentBank) => {
    const { smartComments } = commentBank;
    const newObject = buildClonePayload(commentBank, apiSubjects, {
      smartComments,
    });

    createNewFeedbackBank(newObject)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'items', 'commentBanks'],
        });
        queryClient.invalidateQueries({
          queryKey: ['feedbackTool', 'itemsCount', 'commentBanks'],
        });

        resetCommentBanks();
        resetCommentBanksWithPreferences();
        toast(<Toast message={'feedback bank cloned'} />);
      })
      .catch((error) => {
        console.error('Error cloning feedback bank:', error);
      });
  };

  const downloadCommentBank = (commentBank) => {
    const extractedCommentBank = {
      title: commentBank.title,
      smartComments: commentBank.smartComments,
    };
    exportJsonFile(extractedCommentBank, extractedCommentBank.title, 'cb');
  };

  const handleDownload = (item) => {
    if (item) {
      const extractedCommentBank = {
        title: item.title,
        smartComments: item.smartComments,
      };
      exportJsonFile(extractedCommentBank, extractedCommentBank.title, 'cb');
    }
  };

  const handleClone = (commentBank) => {
    if (commentBank) {
      const { smartComments } = commentBank;
      const newObject = buildClonePayload(commentBank, apiSubjects, {
        smartComments,
      });

      createNewFeedbackBank(newObject)
        .then(async (res) => {
          const selfLink = res?._links?.self?.href;
          const urlParts = selfLink.split('/');
          const newCommentBankId = urlParts[urlParts.length - 1];

          const currentMergedCommentBanks = originalCommentBanks.map((bank) => {
            const preference = commentBanksWithPreferences?.find(
              (pref) => pref.id === bank.id
            );
            return preference
              ? {
                  ...bank,
                  isVisible: preference.isVisible,
                  visible: preference.visible,
                  sortOrder: preference.sortOrder,
                }
              : bank;
          });

          const originalCommentBankIndex = currentMergedCommentBanks.findIndex(
            (bank) => bank.id === commentBank.id
          );
          if (originalCommentBankIndex !== -1) {
            const originalSortOrder = commentBank.sortOrder ?? 999;
            const newSortOrder = originalSortOrder + 1;

            try {
              await updateCommentBankPreferences('REORDER', {
                toolIds: [newCommentBankId],
                newSortOrder,
              });
            } catch (error) {
              console.error(
                'Error setting sort order for duplicated comment bank:',
                error
              );
            }
          }
          queryClient.invalidateQueries({
            queryKey: ['feedbackTool', 'items', 'commentBanks'],
          });
          queryClient.invalidateQueries({
            queryKey: ['feedbackTool', 'itemsCount', 'commentBanks'],
          });
          resetCommentBanks();
          resetCommentBanksWithPreferences();

          toast(<Toast message={'feedback bank cloned'} />);
        })
        .catch((error) => {
          console.error('Cloning error:', error);
          toast(<Toast message={'Cloning failed'} />);
        });
    }
  };

  const handlePreview = (commentBank) => {
    if (commentBank && !isNullOrEmpty(commentBank?.smartComments)) {
      setCommentBankPreviewDialog(true);
      setPreviewCommentBank(commentBank);
    }
  };

  const handleOpenClick = (commentBank) => {
    if (commentBank && !commentBank.isSystem) {
      history.push(`/tools/commentbanks/${commentBank.id}`);
    }
  };

  const deleteCommentBankHandler = (commentBankId) => {
    deleteSmartAnnotation(commentBankId)
      .then(() => {
        resetCommentBanks();
        resetCommentBanksWithPreferences();
        toast(<Toast message={'Comment bank deleted successfully'} />);
      })
      .catch((error) => {
        toast(<Toast message={'Error deleting comment bank'} />);
      });
  };

  const renderCreateOptions = () => (
    <>
      <PopUpCard onClick={() => history.push('/tools/commentbanks/new')}>
        <PopUpCardImg src={PlusIcon} />
        <PopUpCardText>Create Your Own</PopUpCardText>
      </PopUpCard>
      <PopUpCard onClick={() => setShowImportDialog(true)}>
        <PopUpCardImg src={DownloadIcon} />
        <PopUpCardText>Import</PopUpCardText>
      </PopUpCard>
      <PopUpCard
        onClick={() =>
          createSystemFeedbackBank({
            title: 'System Comment Bank',
            smartComments: [],
            isSystem: true,
          })
        }
      >
        <PopUpCardImg src={PlusIcon} />
        <PopUpCardText>Use a template</PopUpCardText>
      </PopUpCard>
    </>
  );

  const renderImportDialog = () => {
    if (showImportDialog) {
      return (
        <CommentBankDialog
          commentBank={importedCommentBank}
          setCommentBankPreviewDialog={setShowImportDialog}
          showActionButton={true}
          onActionButtonClick={() => {
            setShowImportDialog(false);
            createFeedbackBank(importedCommentBank);
          }}
        />
      );
    }
    return null;
  };

  const renderCreatePopup = ({ onClose }) => {
    const selectBankFun = (e, bank) => {
      e.preventDefault();
      setSelectedBank(bank);
      setTimeout(() => {
        if (selectedRef.current) {
          selectedRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      }, 0);
    };

    return (
      <PopupBackground>
        <PopupContainer>
          <PopupTitleContainer>
            <PopupTitle>New Comment Bank</PopupTitle>
            <PopupTitleImg onClick={onClose} src={closecircle} />
          </PopupTitleContainer>
          <PopupDialogContentBox>
            <PopupDialogContentBoxLeft>
              <CardContainer>
                <Card
                  onClick={() => {
                    onClose();
                    history.push('/tools/commentbanks/new');
                  }}
                >
                  <CardImgCont>
                    <CardImg src={PlusIcon} />
                  </CardImgCont>
                  <CardTitle>Create Your Own</CardTitle>
                </Card>
                <ImportFileLabel>
                  <CardImgCont>
                    <CardImg src={UploadIcon} />
                  </CardImgCont>
                  <CardTitle>Import</CardTitle>
                  <ImportFile
                    type="file"
                    accept=".cb"
                    onChange={handleCommentBankImport}
                  />
                </ImportFileLabel>
              </CardContainer>
              {systemSmartAnnotations && systemSmartAnnotations.length > 0 && (
                <BankCommentTitle style={{ borderTop: '1px solid #C9C6CC80' }}>
                  Use a template
                </BankCommentTitle>
              )}

              {systemSmartAnnotations?.map((bank) => {
                return (
                  <Card
                    key={bank.id}
                    onClick={(e) => selectBankFun(e, bank)}
                    ref={bank.id === selectedBank?.id ? selectedRef : null}
                    style={{
                      backgroundColor:
                        bank.id === selectedBank?.id ? ' #F1E6FC' : '#ffffff',
                    }}
                  >
                    <CardImgDoc src={Doc} />
                    <CardTitle>{bank.title}</CardTitle>
                  </Card>
                );
              })}
            </PopupDialogContentBoxLeft>
            <PopupDialogContentBoxRight>
              <PreviewContainer>
                <PreviewIconCont>
                  <PrevieImg src={PreviewIcon} />
                  <Previewpara>Preview</Previewpara>
                </PreviewIconCont>
                {systemSmartAnnotations &&
                  systemSmartAnnotations.length > 0 &&
                  selectedBank && (
                    <CreateButton
                      onClick={() => {
                        createSystemFeedbackBank(selectedBank, onClose);
                      }}
                    >
                      <ButtonText>Add selected template</ButtonText>
                    </CreateButton>
                  )}
              </PreviewContainer>

              {selectedBank?.smartComments?.map((comment) => {
                return (
                  <div key={comment.title}>
                    <BankCommentTitle>{comment.title}</BankCommentTitle>
                    {comment?.suggestions?.map((suggestion) => (
                      <Commentsuggestion key={suggestion}>
                        {suggestion}
                      </Commentsuggestion>
                    ))}
                  </div>
                );
              })}
            </PopupDialogContentBoxRight>
          </PopupDialogContentBox>
        </PopupContainer>
      </PopupBackground>
    );
  };

  const renderItem = (commentBank) => (
    <CommentBankCard
      commentBank={commentBank}
      isSelected={selectedCommentBanks.has(commentBank.id)}
      onSelect={() => handleSelectCommentBank(commentBank.id)}
      showCheckbox={true}
      showActions={true}
      isSystem={commentBank.isSystem}
      cloneCommentBank={cloneCommentBank}
      setCommentBankPreviewDialog={(show) => {
        setCommentBankPreviewDialog(show);
        if (show) {
          setPreviewCommentBank(commentBank);
        }
      }}
      handleDownload={handleDownload}
      handleClone={handleClone}
      handlePreview={handlePreview}
      handleOpenClick={handleOpenClick}
    />
  );

  const mergedCommentBanks = originalCommentBanks.map((bank) => {
    const preference = commentBanksWithPreferences?.find(
      (pref) => pref.id === bank.id
    );
    const merged = preference
      ? {
          ...bank,
          isVisible: preference.isVisible,
          visible: preference.visible,
          sortOrder: preference.sortOrder,
        }
      : bank;
    return merged;
  });

  const filteredItems = mergedCommentBanks.filter((commentBank) => {
    if (isShowSystemCommentBanksOnly) {
      if (commentBank.isSystem) {
        return false;
      }
    }

    if (showHidden) {
      return !commentBank.visible;
    } else {
      return commentBank.visible;
    }
  });

  const resetCommentBanksData = () => {
    resetCommentBanks();
    resetCommentBanksWithPreferences();
  };

  const useReactQueryForList = true;
  const showLoader =
    !useReactQueryForList &&
    hasNoData &&
    (isLoadingCommentBanks || isLoadingCommentBanksWithPreferences);
  if (showLoader) {
    return <Loader />;
  }

  return (
    <>
      <FeedbackToolWrapper
        enableReactQuery={useReactQueryForList}
        context="commentBanks"
        selectedItems={selectedCommentBanks}
        setSelectedCriteria={setSelectedCommentBanks}
        onImport={handleCommentBankImport}
        onSelectAll={handleSelectAll}
        renderItem={renderItem}
        renderCreateOptions={renderCreateOptions}
        renderImportDialog={renderImportDialog}
        isDragDisabled={isDragDisabled}
        hasActiveFilters={hasActiveFilters}
        tabletView={tabletView}
        MenuButton={MenuButton}
        SecondarySidebar={ImprovedSecondarySideBar}
        showHidden={showHidden}
        setShowHidden={setShowHidden}
        showOnlyMineFilter={isShowSystemCommentBanksOnly}
        setShowOnlyMineFilter={setIsShowSystemCommentBanksOnly}
        allMarkingCriteria={mergedCommentBanks}
        resetMarkingCriterias={resetCommentBanksData}
        usePopupForCreate={true}
        renderCreatePopup={renderCreatePopup}
      />
      {commentBankPreviewDialog && previewCommentBank && (
        <CommentBankDialog
          setCommentBankPreviewDialog={setCommentBankPreviewDialog}
          commentBank={previewCommentBank}
          isSystem={previewCommentBank?.isSystem}
        />
      )}
    </>
  );
}

export default CommentBanks;
